import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { MarkerType } from '@apps/main/modules/maps/markerFormatters';
import type {
  FlightIngredient,
  RecipeMainIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  ActivityIngredient,
  HotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import { OwnTransportIngredient } from '@apps/main/modules/recipe/ingredients/ownTransportIngredient';
import type { StayIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  isActivityIngredient,
  isFerryIngredient,
  isFlightIngredient,
  isOwnStayIngredient,
  isOwnTransportIngredient,
  isStayIngredient,
  isTransportIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  CustomerOriginQueryParamValue,
  TripPlannerPage,
} from '@apps/main/new-desktop/core/models';
import {
  useBrowserStore,
  useEditingIngredientStore,
  useGeolocationStore,
  useGlobalCurrencyStore,
  useItineraryStore,
  usePaxStore,
  usePriceCalendarStore,
  useQueryParamsStore,
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
  useUserLocationStore,
} from '@apps/main/new-desktop/core/stores';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import type {
  AddFlightsModalInitialRoute,
  AddFlightsModalRouteStopData,
  AddFligthsModalRoute,
  RouteData,
} from '@apps/main/new-desktop/modules/addFlightsModal';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import { isStena } from '@libs/cookie';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import type { TripOverviewInitiatedMetadata } from '@libs/google-analytics-events';
import { trackTripOverviewInitiated } from '@libs/google-analytics-events';
import { ConnectionType } from '@libs/maps';
import type {
  AvailableFerryIngredientResponse,
  AvailableFlightIngredientResponse,
  HeartedHotel,
  IngredientId,
  PortInfo,
  SearchTerm,
} from '@libs/trip-planner-interfaces';
import {
  AvailabilityOrder,
  MyPositionType,
  TransportType,
  isBookableTransportType,
  isOwnTransportType,
} from '@libs/trip-planner-interfaces';
import {
  assertValue,
  exhaustiveGuard,
  isBookableTransport,
  readonlyRef,
} from '@libs/utils';

import type {
  AlternativeRoomSuggestionAccommodationSegment,
  AlternativeTicketSuggestionActivitySegment,
  ItinerartBookableSegmentType,
  ItineraryBookableTransportSegment,
  ItineraryLoaderSegment,
  ItineraryLocationLoaderSegment,
  ItineraryLocationSegment,
  ItineraryNoTransportLoaderSegment,
  ItineraryNoTransportSegment,
  ItinerarySegment,
  ItinerarySimpleTransportSegment,
  ItineraryStenaUpsellNoTransportSegment,
  ItineraryTransportLoaderSegment,
  ItineraryUpsellNoTransportSegment,
  TripOverviewSegments,
  UnavailabilityReason,
} from '../models';
import {
  ItinerarySegmentType,
  TripOverviewTemplateIds,
  isItineraryBookableTransport,
} from '../models';

import { getTripItineraryLoaderSegments } from './tripOverviewLoaderLogic';
import { useTripOverviewModalsStore } from './tripOverviewModalsStore';
import { getTripOverviewSegments } from './tripOverviewSegments';
import { getUnavailableActivityMessage } from './tripOverviewSegments/tripOverviewActivitySegmentsLogic';
import {
  getUnavailableFerryMessage,
  getUnavailableFlightMessage,
} from './tripOverviewSegments/tripOverviewBookableTransportSegmentLogic';
import { getUnavailableAccommodationMessage } from './tripOverviewSegments/tripOverviewHotelRelatedSegmentsLogic';
import { mapTransportTypeToConnectionType } from './tripOverviewSegments/tripOverviewStoreSegmentsLogic';
import {
  getAutocompleteTypeFromLocationSegment,
  getFerryDetails,
  getFlightDetails,
  getHomeFlightData,
  getSearchTextFromLocationSegment,
  updateFlightIngredientsWithAlternativeSuggestion,
} from './tripOverviewStoreLogic';
import { useUpsellStore } from './upsell/upsellStore';

export const useTripOverviewPageStore = defineStore(
  'TripOverviewPageStore',
  () => {
    const mapStore = useMapStore();
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();
    const itineraryStore = useItineraryStore();
    const upsellStore = useUpsellStore();
    const routerStore = useRouterStore();
    const tripOverviewModalsStore = useTripOverviewModalsStore();
    const editingIngredientStore = useEditingIngredientStore();
    const geolocationStore = useGeolocationStore();
    const statsigEventsStore = useStatsigEventsStore();
    const preferredTimeStore = usePreferredTimeStore();
    const browserStore = useBrowserStore();
    const userLocationStore = useUserLocationStore();
    const priceCalendarStore = usePriceCalendarStore();
    const queryParamsStore = useQueryParamsStore();
    const globalCurrencyStore = useGlobalCurrencyStore();
    const paxStore = usePaxStore();

    const isFirstTripItineraryRender =
      routerStore.landingRoute === TripPlannerPage.TripOverview;

    const hasUserSelectedTransport = ref<boolean>(false);

    // Actions
    function initializeView(): void {
      loadUserGeolocationToMapState();
      const metadata: TripOverviewInitiatedMetadata = {
        totalAccomodation: recipeStore.hotelOnlyRecipe.length,
        totalFerry: recipeStore.ferryOnlyRecipe.length,
        totalFlight: recipeStore.flightOnlyRecipe.length,
        totalActivity: recipeStore.activityOnlyRecipe.length,
      };
      statsigEventsStore.logTripOverviewInitiated(metadata);
      trackTripOverviewInitiated(metadata);
    }

    function setUserSelectedTransport(value: boolean): void {
      hasUserSelectedTransport.value = value;
    }

    function deleteIngredientById(ingredientId: IngredientId): void {
      recipeStore.deleteIngredientById(ingredientId);
    }

    function confirmAlternativeFlight(ingredientId: IngredientId): void {
      const flightIngredientResponse =
        itineraryStore.getFlightIngredientResponse(ingredientId);

      const flightResponses =
        itineraryStore.selectedItinerary?.flightOnlyItinerary;

      assertValue(
        flightResponses,
        'tripOverviewPageStore.ts > confirmAlternativeFlight > flightResponses'
      );

      const flightsInPackage = flightResponses.filter(
        (flight) =>
          flight.alternativeSuggestion?.addToBasketToken ===
          flightIngredientResponse?.alternativeSuggestion?.addToBasketToken
      );

      flightsInPackage.forEach((flight) => {
        const alternativeFlight = flight.alternativeSuggestion;

        assertValue(
          alternativeFlight,
          'tripOverviewPageStore.ts > confirmAlternativeFlight > alternativeFlight'
        );

        updateFlightIngredientsWithAlternativeSuggestion(
          flight.ingredientId,
          alternativeFlight,
          recipeStore.getFlightIngredient
        );
      });
    }

    function getFromPortInfoFromEditingIngredient(): PortInfo | undefined {
      const ingredient = editingIngredientStore.editingTransportIngredient;
      if (!ingredient) {
        return undefined;
      }
      return {
        code: ingredient.fromPort,
        label: ingredient.fromPortDisplayText,
        name: ingredient.fromPortText,
      };
    }

    function getToPortInfoFromEditingIngredient(): PortInfo | undefined {
      const ingredient = editingIngredientStore.editingTransportIngredient;
      if (!ingredient) {
        return undefined;
      }
      return {
        code: ingredient.toPort,
        label: ingredient.toPortDisplayText,
        name: ingredient.toPortText,
      };
    }

    // Getters

    const numberOfDestinations = computed<number>(
      () => recipeStore.stayOnlyRecipe.length
    );

    const numberOfNights = computed<number>(() =>
      recipeStore.stayOnlyRecipe.reduce((acc, hotel) => {
        if (hotel.numberOfNights) {
          acc += hotel.numberOfNights;
        }
        return acc;
      }, 0)
    );

    const hasRecipeIngredients = computed(
      () => recipeStore.hasRecipeIngredients
    );

    const homeFlightData = computed(() =>
      getHomeFlightData(recipeStore.recipe)
    );

    const tripItinerary = computed<ItinerarySegment[]>(() => {
      const tripOverviewSegments: TripOverviewSegments = {
        chronologicalItinerary: tripStore.chronologicalItinerary,
        startLocation: userLocationStore.startLocation,
        endLocation: userLocationStore.endLocation,
        getIngredient: recipeStore.getIngredient,
        getIngredientIndex: recipeStore.getIngredientIndex,
        paxes: paxStore.pax.rooms,
        isSuggestedHotel: tripStore.isSuggestedHotel,
        isSuggestedTransport: tripStore.isSuggestedTransport,
        endLocationIngredient: recipeStore.endLocationIngredient,
        calculatedRoute: mapStore.calculatedRoute,
        ferryTicketCategoryDictionary:
          upsellStore.ferryTicketCategoryDictionary,
        userSelectedFerryTicketCategory:
          upsellStore.userSelectedFerryTicketCategory,
        ferryVehicleTypes: tripStore.ferryVehicleTypes,
        areThereActivitiesInDestination:
          tripStore.areThereActivitiesInDestination,
        getActivitySuggestions: itineraryStore.getActivitySuggestions,
        getStaticActivitySuggestions:
          itineraryStore.getDestinationSuggestedActivities,
        selectedDate: preferredTimeStore.preferredDay,
        getFlightAllCarriers: (ingredientId: IngredientId) =>
          tripStore.getFlightAllCarriers(ingredientId).allCarriers,
        getFerryAllCarriers: (ingredientId: IngredientId) =>
          tripStore.getFerryAllCarriers(ingredientId).allCarriers,
        isUserFromBookingPage:
          queryParamsStore.queryParams?.customerOrigin ===
          CustomerOriginQueryParamValue.BookingPage,
        hasUserSelectedTransport: hasUserSelectedTransport.value,
        isUserFromStena: isStena(),
        currency: globalCurrencyStore.globalCurrency || '',
        moneyFormanterFn: globalCurrencyStore.formatMoney,
        destinationAccommodationsAmount:
          tripStore.destinationAccommodationsAmount,
      };
      const result = getTripOverviewSegments(tripOverviewSegments);

      return result;
    });

    const isLoading = computed(() => itineraryStore.isSearchingItineraries);
    const isLoadingSimpleTransportSegmentDetails = computed(
      () => mapStore.isLoadingCalculatedRoute || isFeatureToggleActive('loader')
    );

    const anyItineraryLoaded = computed(() => tripItinerary.value.length > 0);

    const tripItineraryLoader = computed<ItineraryLoaderSegment[]>(() =>
      getTripItineraryLoaderSegments(
        recipeStore.recipe,
        !!geolocationStore.userGeolocation,
        browserStore.isSmallerThan900px,
        (ingredientId: IngredientId) =>
          tripStore.getFlightAllCarriers(ingredientId).allCarriers,
        (ingredientId: IngredientId) =>
          tripStore.getFerryAllCarriers(ingredientId).allCarriers,
        globalCurrencyStore.globalCurrency || ''
      )
    );

    function selectTransport(
      segment:
        | ItineraryNoTransportSegment
        | ItineraryBookableTransportSegment
        | ItinerarySimpleTransportSegment
        | ItineraryTransportLoaderSegment
        | ItineraryNoTransportLoaderSegment
        | ItineraryUpsellNoTransportSegment
        | ItineraryStenaUpsellNoTransportSegment,
      newTransportType: TransportType
    ): void {
      if (
        segment.segmentType === ItinerarySegmentType.Flight &&
        newTransportType !== TransportType.Flight
      ) {
        statsigEventsStore.logTripOverviewFlightRemoved();
      }

      if (
        segment.segmentType === ItinerarySegmentType.Ferry &&
        newTransportType !== TransportType.Ferry
      ) {
        statsigEventsStore.logTripOverviewFerryRemoved();
      }

      const ingredient = getIngredientForSelectTransport(segment);
      const newTransportConnectionType =
        mapTransportTypeToConnectionType(newTransportType);

      const ingredientIndex = recipeStore.getIngredientIndex(
        segment.ingredientId
      );
      const isLastTransport = ingredientIndex === recipeStore.recipe.length - 1;
      setEditingIngredient(ingredient);

      if (
        ingredient &&
        segment.segmentType.toLocaleLowerCase() ===
          newTransportType.toLocaleLowerCase()
      ) {
        if (segment.segmentType === ItinerarySegmentType.Flight) {
          routerStore.navigateToFlightsList();

          return;
        }
        if (segment.segmentType === ItinerarySegmentType.Ferry) {
          routerStore.navigateToFerryList();

          return;
        }
        if (
          isItineraryBookableTransport(segment) &&
          isTransportIngredient(ingredient)
        ) {
          editingIngredientStore.setTransportEditingIngredient(
            ingredient,
            'recipe'
          );
        }
        return;
      }

      if (isItineraryBookableTransport(segment)) {
        if (ingredient) {
          recipeStore.deleteIngredient(ingredient);

          // HACK: https://trello.com/c/ykysGVjv to avoid this bug when user is switching from bookable transport to other bookable transport
          //  and do not confirm new bookable transport, we need to replace old bookable transport with own transport  with type NONE
          const newOwnTransportIngredient = new OwnTransportIngredient(
            TransportType.None
          );
          recipeStore.addIngredient(newOwnTransportIngredient, ingredientIndex);
        }
        if (isLastTransport) {
          const endLocationIngredient = recipeStore.endLocationIngredient;

          setEditingIngredient(endLocationIngredient);
        } else {
          const newIngredientToSetTransport =
            recipeStore.recipe[ingredientIndex];
          setEditingIngredient(newIngredientToSetTransport);
        }
      }
      if (
        isBookableTransport(newTransportConnectionType) &&
        isBookableTransportType(newTransportType)
      ) {
        recipeStore.createTransportIngredient(newTransportConnectionType);

        if (ingredient) {
          if (isOwnTransportIngredient(ingredient)) {
            ingredient.setOwnTransportType(TransportType.None);
          }
        }
        if (newTransportConnectionType === ConnectionType.FLIGHT) {
          tripOverviewModalsStore.openAddFlightsModal();
        } else {
          tripOverviewModalsStore.openPortSelectionModal(
            editingIngredientStore.editingTransportIngredient?.ingredientId,
            getFromPortInfoFromEditingIngredient(),
            getToPortInfoFromEditingIngredient(),
            newTransportType,
            false
          );
        }
      } else {
        if (isOwnTransportType(newTransportType) && ingredient) {
          if (isOwnTransportIngredient(ingredient)) {
            ingredient.setOwnTransportType(newTransportType);
          } else {
            const newOwnTransportIngredient = new OwnTransportIngredient(
              newTransportType
            );
            recipeStore.addIngredient(
              newOwnTransportIngredient,
              ingredientIndex
            );
          }
        }
      }
    }

    function getIngredientForSelectTransport(
      segment:
        | ItineraryNoTransportSegment
        | ItineraryBookableTransportSegment
        | ItinerarySimpleTransportSegment
        | ItineraryTransportLoaderSegment
        | ItineraryNoTransportLoaderSegment
        | ItineraryUpsellNoTransportSegment
        | ItineraryStenaUpsellNoTransportSegment
    ): RecipeMainIngredient | undefined {
      let ingredient: RecipeMainIngredient | undefined;

      const recipeIngredient = recipeStore.getIngredient(segment.ingredientId);
      if (
        recipeIngredient &&
        (isTransportIngredient(recipeIngredient) ||
          isStayIngredient(recipeIngredient) ||
          isOwnTransportIngredient(recipeIngredient))
      ) {
        ingredient = recipeIngredient;
      }

      return ingredient;
    }

    function setEditingIngredient(
      ingredient: RecipeMainIngredient | undefined
    ): void {
      if (ingredient) {
        recipeStore.editIngredientTransport(ingredient);
      }
    }

    function closePortSelectionModal(): void {
      editingIngredientStore.doneEditingIngredient();
      tripOverviewModalsStore.closePortSelectionModal();
    }

    function getAddFlightsModalRoutes(): AddFlightsModalInitialRoute[] {
      const routes: AddFlightsModalInitialRoute[] = [];
      let previousStay: StayIngredient | undefined;

      recipeStore.stayOnlyRecipe.forEach((currentStay, index) => {
        const previousRouteData = getPreviousRouteData(
          currentStay,
          previousStay
        );
        const nextRouteData = getNextRouteData(currentStay);
        const previousIngredient = recipeStore.getPreviousIngredient(
          currentStay.ingredientId
        );

        const isPreviousNoTransport =
          !!previousIngredient &&
          isOwnTransportIngredient(previousIngredient) &&
          previousIngredient.ownTransportType === TransportType.None;

        const isPreviousFlightTransport =
          !!previousIngredient && isFlightIngredient(previousIngredient);

        routes.push({
          from: previousRouteData.from,
          to: previousRouteData.to,
          ingredientId: currentStay.ingredientId,
          isLast: false,
          numberOfNights: currentStay.numberOfNights,
          isEnabled: isPreviousNoTransport || isPreviousFlightTransport,
          isDirectFlight:
            isPreviousFlightTransport &&
            previousIngredient.filters.maxNumberOfStops === 0,
        });

        if (index === recipeStore.stayOnlyRecipe.length - 1) {
          const nextIngredient = recipeStore.getNextIngredient(
            currentStay.ingredientId
          );

          const lastRecipeIngredient = recipeStore.recipe.at(-1);

          const isLastNoTransport =
            !!lastRecipeIngredient &&
            isOwnTransportIngredient(lastRecipeIngredient) &&
            lastRecipeIngredient.ownTransportType === TransportType.None;

          const isLastFlightTransport =
            !!nextIngredient && isFlightIngredient(nextIngredient);

          routes.push({
            from: nextRouteData.from,
            to: nextRouteData.to,
            ingredientId: currentStay.ingredientId,
            isLast: true,
            numberOfNights: currentStay.numberOfNights,
            isEnabled: isLastFlightTransport || isLastNoTransport,
            isDirectFlight:
              isLastFlightTransport &&
              nextIngredient.filters.maxNumberOfStops === 0,
          });
        }

        previousStay = currentStay;
      });

      return routes;
    }

    function getPreviousRouteData(
      currentStay: StayIngredient,
      previousStay?: StayIngredient
    ): RouteData {
      const previousIngredient = recipeStore.getPreviousIngredient(
        currentStay.ingredientId
      );
      const hasPreviousFlight =
        previousIngredient && isFlightIngredient(previousIngredient);

      const currentStayHotelMainPolygonId = tripStore.getMainPolygonId(
        currentStay.ingredientId
      );

      const currentStayHotelSearchText: string | undefined =
        currentStayHotelMainPolygonId
          ? getSearchTextFromPolygon(currentStayHotelMainPolygonId)
          : undefined;

      const previousStayHotelMainPolygonId =
        previousStay && tripStore.getMainPolygonId(previousStay.ingredientId);

      const previousStayHotelSearchText: string | undefined =
        previousStayHotelMainPolygonId
          ? getSearchTextFromPolygon(previousStayHotelMainPolygonId)
          : undefined;

      return {
        from: hasPreviousFlight
          ? getRouteDataFromTransportIngredient(previousIngredient, 'from')
          : previousStay
          ? getStayRouteData(previousStay, previousStayHotelSearchText)
          : undefined,
        to: hasPreviousFlight
          ? getRouteDataFromTransportIngredient(previousIngredient, 'to')
          : getStayRouteData(currentStay, currentStayHotelSearchText),
      };
    }

    function getSearchTextFromPolygon(polygonId: number): string {
      return `polygon-id:${polygonId}`;
    }

    function getNextRouteData(currentStay: StayIngredient): RouteData {
      const nextIngredient = recipeStore.getNextIngredient(
        currentStay.ingredientId
      );
      const hasNextFlight =
        nextIngredient && isFlightIngredient(nextIngredient);

      const currentStayHotelMainPolygonId = tripStore.getMainPolygonId(
        currentStay.ingredientId
      );

      const currentStayHotelSearchText: string | undefined =
        currentStayHotelMainPolygonId
          ? getSearchTextFromPolygon(currentStayHotelMainPolygonId)
          : undefined;

      return {
        from: hasNextFlight
          ? getRouteDataFromTransportIngredient(nextIngredient, 'from')
          : getStayRouteData(currentStay, currentStayHotelSearchText),
        to: hasNextFlight
          ? getRouteDataFromTransportIngredient(nextIngredient, 'to')
          : undefined,
      };
    }

    function getRouteDataFromTransportIngredient(
      ingredient: FlightIngredient,
      direction: 'from' | 'to'
    ): AddFlightsModalRouteStopData {
      return {
        displayText:
          direction === 'from'
            ? ingredient.fromPortDisplayText ?? ''
            : ingredient.toPortDisplayText ?? '',
        searchText:
          direction === 'from'
            ? ingredient.fromPortText ?? ''
            : ingredient.toPortText ?? '',
      };
    }

    function getStayRouteData(
      stay: StayIngredient,
      searchText?: string
    ): AddFlightsModalRouteStopData {
      return {
        displayText: stay.displayText,
        searchText: searchText || stay.getSearchText(),
      };
    }

    function confirmAddFlightsModal(routes: AddFligthsModalRoute[]): void {
      tripOverviewModalsStore.closeAddFlightsModal();
      routes.forEach((route) => {
        const ingredient = recipeStore.getStayIngredient(route.ingredientId);

        const stayIndex = recipeStore.getIngredientIndex(
          ingredient.ingredientId
        );

        const previousIngredient = route.isLast
          ? recipeStore.recipe[recipeStore.recipe.length - 1]
          : recipeStore.recipe[stayIndex - 1];

        const isPreviousIngredientFlight = previousIngredient
          ? isFlightIngredient(previousIngredient)
          : false;

        const isPreviousFerryIngredient = previousIngredient
          ? isFerryIngredient(previousIngredient)
          : false;

        const isPreviousOwnTransportIngredient = previousIngredient
          ? isOwnTransportIngredient(previousIngredient)
          : false;

        if (!route.isEnabled) {
          if (isPreviousIngredientFlight) {
            const newOwnTransportIngredient = new OwnTransportIngredient(
              TransportType.None
            );
            if (route.isLast) {
              recipeStore.recipe.splice(
                recipeStore.recipe.length - 1,
                1,
                newOwnTransportIngredient
              );
            } else {
              recipeStore.recipe.splice(
                stayIndex - 1,
                1,
                newOwnTransportIngredient
              );
            }
          }
          return;
        }
        if (route.isLast) {
          const endLocationIngredient = recipeStore.endLocationIngredient;

          setEditingIngredient(endLocationIngredient);
        } else {
          setEditingIngredient(ingredient);
        }

        recipeStore.createTransportIngredient(ConnectionType.FLIGHT);
        const newIngredientId =
          recipeStore.settingTransportIngredient?.ingredientId;
        if (route.fromAirport.code && route.fromAirport.label) {
          editingIngredientStore.editingFlightIngredient?.setDepartureAirport({
            iata: route.fromAirport.code,
            name: route.fromAirport.name,
            displayingText: route.fromAirport.label,
          });
        }

        if (route.toAirport.code && route.toAirport.label) {
          editingIngredientStore.editingFlightIngredient?.setArrivalAirport({
            iata: route.toAirport.code,
            name: route.toAirport.name,
            displayingText: route.toAirport.label,
          });
        }

        if (editingIngredientStore.editingTransportIngredient) {
          if (newIngredientId) {
            const ingredientIndex =
              recipeStore.getIngredientIndex(newIngredientId);
            if (
              isPreviousIngredientFlight ||
              isPreviousFerryIngredient ||
              isPreviousOwnTransportIngredient
            ) {
              if (route.isLast) {
                const ingredientToReplace =
                  recipeStore.recipe[recipeStore.recipe.length - 1];
                if (ingredientToReplace) {
                  recipeStore.replaceIngredient(
                    ingredientToReplace,
                    editingIngredientStore.editingTransportIngredient
                  );
                }
              } else {
                const ingredientToReplace = recipeStore.recipe[stayIndex - 1];
                if (ingredientToReplace) {
                  recipeStore.replaceIngredient(
                    ingredientToReplace,
                    editingIngredientStore.editingTransportIngredient
                  );
                }
              }
            } else {
              recipeStore.addIngredient(
                editingIngredientStore.editingTransportIngredient,
                ingredientIndex
              );
            }
            if (editingIngredientStore.editingFlightIngredient) {
              editingIngredientStore.editingFlightIngredient.filters.maxNumberOfStops =
                route.isDirect ? 0 : undefined;
            }
            editingIngredientStore.doneEditingIngredient();
          }
        }
      });
    }

    function showFerryDetailsModal(ingredientId: IngredientId): void {
      const ferryResponse = itineraryStore.getTransportIngredientResponse(
        ingredientId
      ) as AvailableFerryIngredientResponse;
      const ferryStaticInfo = tripStore.getTransportStaticInfo(ferryResponse);

      const hasUserSelectedFerryTicketCategory =
        upsellStore.userSelectedFerryTicketCategory?.[
          ferryResponse.addToBasketToken
        ] !== undefined;
      const ticketType = hasUserSelectedFerryTicketCategory
        ? upsellStore.userSelectedFerryTicketCategory?.[
            ferryResponse.addToBasketToken
          ]
        : upsellStore.ferryTicketCategoryDictionary?.[
            ferryResponse.addToBasketToken
          ]?.[0];

      const ferryDetails = getFerryDetails(
        ferryResponse,
        ferryStaticInfo,
        ticketType?.type,
        tripStore.ferryVehicleTypes
      );
      if (ferryDetails) {
        tripOverviewModalsStore.openFerryDetailsModal(ferryDetails);
      } else {
        throw new Error("Can't open ferry details!");
      }
    }

    function showFlightDetailsModal(ingredientId: IngredientId): void {
      const flightResponse = itineraryStore.getTransportIngredientResponse(
        ingredientId
      ) as AvailableFlightIngredientResponse;

      const flightStaticInfo = tripStore.getTransportStaticInfo(flightResponse);

      const flightDetails = getFlightDetails(flightResponse, flightStaticInfo);
      if (flightDetails) {
        tripOverviewModalsStore.openFlightDetailsModal(flightDetails);
      } else {
        throw new Error("Can't open flight details!");
      }
    }

    function loadUserGeolocationToMapState(): void {
      if (geolocationStore.userGeolocation) {
        return;
      }

      geolocationStore.loadUserCoordinates().then(() => {
        const coordinates = geolocationStore.userGeolocation;

        if (coordinates) {
          if (!mapStore.startingLocation) {
            mapStore.setPointLocationFromPosition(
              coordinates,
              MyPositionType.Start,
              MarkerType.Location
            );
          }

          if (!mapStore.endLocation) {
            mapStore.setPointLocationFromPosition(
              coordinates,
              MyPositionType.End,
              MarkerType.Location
            );
          }
        }
      });
    }

    function scrollToCalendar(): void {
      const thresholdToPulse = 1000;
      const calendarElement = document.getElementById(
        TripOverviewTemplateIds.PriceCalendar
      );

      assertValue(calendarElement);

      calendarElement.scrollIntoView({
        behavior: 'smooth',
      });

      setTimeout(() => {
        priceCalendarStore.showPulsingIndicator();
      }, thresholdToPulse);
    }

    function getUnavailabilityMessage(
      segmentType: ItinerartBookableSegmentType,
      reasons: UnavailabilityReason[]
    ): string {
      switch (segmentType) {
        case ItinerarySegmentType.Accommodation:
          return getUnavailableAccommodationMessage(reasons);

        case ItinerarySegmentType.Activity:
          return getUnavailableActivityMessage(reasons);

        case ItinerarySegmentType.Flight:
          return getUnavailableFlightMessage(reasons);

        case ItinerarySegmentType.Ferry:
          return getUnavailableFerryMessage(reasons);

        default:
          exhaustiveGuard(
            segmentType,
            `No unavailability message for this segment type: ${segmentType}`
          );
      }
    }

    function changeLocation(
      segment: ItineraryLocationSegment | ItineraryLocationLoaderSegment
    ): void {
      const searchText = getSearchTextFromLocationSegment(segment.location);
      const modalType = getAutocompleteTypeFromLocationSegment(segment);
      tripOverviewModalsStore.openDestinationSelectorModal(
        searchText,
        undefined,
        modalType
      );
    }

    function confirmAlternativeRoomSelection(
      segment: AlternativeRoomSuggestionAccommodationSegment
    ): void {
      statsigEventsStore.logTripOverviewAlternativeStaySelected();

      const heartedHotel: HeartedHotel = {
        code: segment.alternativeSuggestionData.hotelCode,
        isRefundable: segment.alternativeSuggestionData.isRefundable,
        rooms: segment.alternativeSuggestionData.rooms.map((room) => ({
          code: room.roomCode,
          boardBasis: room.mealCode,
        })),
      };

      tripStore.toggleHeartHotel(heartedHotel, segment.context.ingredientId);
      mapStore.updateMapShouldCenterOnMarkers(true);
      editingIngredientStore.doneEditingIngredient();
    }

    function confirmAlternativeTicketSelection(
      segment: AlternativeTicketSuggestionActivitySegment
    ): void {
      statsigEventsStore.logTripOverviewAlternativeActivitySelected();

      const activityIngredient = recipeStore.getActivityIngredient(
        segment.context.ingredientId
      );

      activityIngredient.tickets = segment.alternativeSuggestionData.tickets;
    }

    const stopWatch = watch(isLoading, () => {
      const hasLandedInTripOverview =
        routerStore.landingRoute === TripPlannerPage.TripOverview;

      if (!hasLandedInTripOverview) {
        stopWatch();
        return;
      }

      if (!isLoading.value) {
        const isInfiniteLoad = !anyItineraryLoaded.value;

        if (isInfiniteLoad) {
          statsigEventsStore.logTripOverviewInfiniteLoad();
        }

        stopWatch();
      }
    });

    function addAccommodationToOwnStayIngredient(
      ownStayIngredientId: IngredientId
    ): void {
      const ownStayIngredient =
        recipeStore.getOwnStayIngredient(ownStayIngredientId);
      setDestinationTextToOwnStayIngredient(
        ownStayIngredient.ingredientId,
        ownStayIngredient.searchTerm,
        ownStayIngredient.displayText
      );
    }

    function setDestinationTextToOwnStayIngredient(
      ownStayIngredientId: IngredientId,
      newSearchTerm: SearchTerm,
      newDisplayText?: string
    ): void {
      const ownStayIngredient =
        recipeStore.getOwnStayIngredient(ownStayIngredientId);

      const hotelIngredient = new HotelIngredient(newSearchTerm);
      hotelIngredient.numberOfNights = ownStayIngredient.numberOfNights;
      hotelIngredient.subIngredients = ownStayIngredient.subIngredients.map(
        (activitySubIngredient) => {
          const newActivitySubIngredient = new ActivityIngredient(
            activitySubIngredient.code,
            hotelIngredient.ingredientId
          );

          newActivitySubIngredient.offset = activitySubIngredient.offset;
          newActivitySubIngredient.tickets = activitySubIngredient.tickets;

          return newActivitySubIngredient;
        }
      );

      if (newDisplayText) {
        hotelIngredient.displayText = newDisplayText;
      }

      recipeStore.replaceIngredient(ownStayIngredient, hotelIngredient);
    }

    function updateSuggestionType(
      ingredientId: IngredientId,
      suggestionType: SuggestionType
    ): void {
      const ingredient = recipeStore.getIngredient(ingredientId);

      if (
        isOwnStayIngredient(ingredient) ||
        isActivityIngredient(ingredient) ||
        isOwnTransportIngredient(ingredient)
      ) {
        return;
      }

      const availabilityOrder =
        suggestionType === SuggestionType.MostPopular
          ? AvailabilityOrder.Popular
          : AvailabilityOrder.Cheapest;
      ingredient.availabilityOrder = availabilityOrder;
    }

    function getFirstDestinationName(): string | undefined {
      const firstDestination = recipeStore.stayOnlyRecipe[0];
      if (firstDestination) {
        return getCityText(firstDestination.displayText);
      }
      return undefined;
    }

    function getCityText(displayText: string): string {
      const destinationText = displayText;

      return destinationText.indexOf(',') > -1
        ? destinationText.slice(0, destinationText.lastIndexOf(',')).trim()
        : destinationText.trim();
    }

    return {
      // State
      isFirstTripItineraryRender,

      // Actions
      setUserSelectedTransport,
      initializeView,
      deleteIngredientById,
      selectTransport,
      closePortSelectionModal,
      getAddFlightsModalRoutes,
      showFerryDetailsModal,
      showFlightDetailsModal,
      confirmAddFlightsModal,
      scrollToCalendar,
      getUnavailabilityMessage,
      changeLocation,
      confirmAlternativeRoomSelection,
      confirmAlternativeTicketSelection,
      confirmAlternativeFlight,
      addAccommodationToOwnStayIngredient,
      setDestinationTextToOwnStayIngredient,
      updateSuggestionType,
      getFirstDestinationName,

      // Getters
      hasUserSelectedTransport: readonlyRef(hasUserSelectedTransport),
      isLoading,
      isLoadingSimpleTransportSegmentDetails,
      anyItineraryLoaded,
      hasRecipeIngredients,
      tripItinerary,
      tripItineraryLoader,
      homeFlightData,
      numberOfDestinations,
      numberOfNights,
    };
  }
);
